import React, {useState} from "react";
import {useSelector} from "react-redux";
import {useFormikContext} from "formik";

import {IStore} from "../../app/reducers/hybrid_reducer";

const S3_BUCKET = "s3-zakupauto-service";
const REGION = "eu-central-1";

interface UploadResponse {
    url: string;
    filename: string;
}

interface WpGalleryImage {
    ID?: number;
    id?: number;
    url?: string;
    sizes?: {
        thumbnail?: string;
        medium?: string;
        large?: string;
    };
}

interface GalleryFormValues {
    filekeys?: string;
    gallery: WpGalleryImage[];
    gallery_order: string[];
}

type GalleryItem =
    | {source: "wp"; id: number; preview: string; image: WpGalleryImage}
    | {source: "s3"; key: string; preview: string};

const s3Url = (key: string) => `https://${S3_BUCKET}.s3.${REGION}.amazonaws.com/${key}`;
const itemToken = (item: GalleryItem) => (item.source === "wp" ? `wp:${item.id}` : `s3:${item.key}`);

const buildGalleryItems = (values: GalleryFormValues): GalleryItem[] => {
    const wpItems: GalleryItem[] = (Array.isArray(values.gallery) ? values.gallery : [])
        .map((image) => {
            const id = image.id ?? image.ID;
            if (typeof id !== "number") {
                return null;
            }
            return {
                source: "wp" as const,
                id,
                image,
                preview: image.sizes?.medium || image.sizes?.thumbnail || image.url || ""
            };
        })
        .filter((item): item is Extract<GalleryItem, {source: "wp"}> => item !== null);
    const s3Items: GalleryItem[] = (values.filekeys || "")
        .split("|")
        .filter(Boolean)
        .map((key) => ({source: "s3" as const, key, preview: s3Url(key)}));
    const allItems = [...wpItems, ...s3Items];

    if (!Array.isArray(values.gallery_order) || values.gallery_order.length === 0) {
        return allItems;
    }

    const byToken = new Map(allItems.map((item) => [itemToken(item), item]));
    const ordered = values.gallery_order.map((token) => byToken.get(token)).filter((item): item is GalleryItem => Boolean(item));
    const usedTokens = new Set(ordered.map(itemToken));
    return [...ordered, ...allItems.filter((item) => !usedTokens.has(itemToken(item)))];
};

const EditUploadImageToS3 = () => {
    const {values, setFieldValue} = useFormikContext<GalleryFormValues>();
    const userData = useSelector((store: IStore) => store.dashboard.userData);
    const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const items = buildGalleryItems(values);

    const syncItems = (nextItems: GalleryItem[]) => {
        setFieldValue(
            "gallery",
            nextItems.filter((item): item is Extract<GalleryItem, {source: "wp"}> => item.source === "wp").map((item) => item.image)
        );
        setFieldValue(
            "filekeys",
            nextItems
                .filter((item): item is Extract<GalleryItem, {source: "s3"}> => item.source === "s3")
                .map((item) => item.key)
                .join("|")
        );
        setFieldValue("gallery_order", nextItems.map(itemToken));
    };

    const uploadFile = async (file: File): Promise<GalleryItem | null> => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("userID", userData.data.user_id?.toString() || "");

        try {
            const response = await fetch("https://rw-web-api.eu/wp-json/wp/v2/upload_gallery_to_s3/", {
                method: "POST",
                body: formData
            });

            const data: UploadResponse = await response.json();

            if (response.ok) {
                return {source: "s3", key: data.filename, preview: data.url || s3Url(data.filename)};
            } else {
                console.error("Error uploading file:", data);
                setError(`Nie udało się dodać pliku ${file.name}.`);
            }
        } catch (error) {
            console.warn("err on uploadToS3", error);
            setError(`Nie udało się dodać pliku ${file.name}.`);
        }
        return null;
    };

    const handleRemoveImage = async (item: GalleryItem) => {
        if (item.source === "wp") {
            syncItems(items.filter((candidate) => itemToken(candidate) !== itemToken(item)));
            return;
        }

        const formData = new FormData();
        formData.append("key", item.key);
        try {
            const response = await fetch("https://rw-web-api.eu/wp-json/wp/v2/delete_from_s3/", {
                method: "POST",
                body: formData
            });

            const data: any = await response.json();

            if (response.ok) {
                syncItems(items.filter((candidate) => itemToken(candidate) !== itemToken(item)));
            } else {
                console.error("Error deleting file:", data);
                setError("Nie udało się usunąć zdjęcia.");
            }
        } catch (error) {
            console.warn("err on delete from s3 front ", error);
            setError("Nie udało się usunąć zdjęcia.");
        }
    };

    const handleFileInput = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files || files.length === 0) {
            return;
        }
        setUploading(true);
        setError(null);
        const nextItems = [...items];
        for (const file of Array.from(files)) {
            const uploadedItem = await uploadFile(file);
            if (uploadedItem) {
                nextItems.push(uploadedItem);
                syncItems(nextItems);
            }
        }
        setUploading(false);
        e.target.value = "";
    };

    const moveItem = (from: number, to: number) => {
        if (from === to) {
            return;
        }
        const nextItems = [...items];
        const [movedItem] = nextItems.splice(from, 1);
        nextItems.splice(to, 0, movedItem);
        syncItems(nextItems);
    };

    const handleDrop = (targetIndex: number) => {
        if (draggedIndex !== null) {
            moveItem(draggedIndex, targetIndex);
        }
        setDraggedIndex(null);
    };

    return (
        <div>
            <label>
                Dodaj zdjęcia
                <input type="file" accept="image/*" multiple onChange={handleFileInput} disabled={uploading} />
            </label>
            <p style={{margin: "8px 0"}}>Przeciągnij zdjęcia, aby zmienić kolejność. Pierwsze zdjęcie jest zdjęciem głównym.</p>
            {error && <p style={{color: "red"}}>{error}</p>}
            <div style={{display: "flex", flexWrap: "wrap", gap: "12px", marginTop: "20px"}}>
                {items.map((item, index) => (
                    <div
                        key={itemToken(item)}
                        draggable
                        onDragStart={() => setDraggedIndex(index)}
                        onDragEnd={() => setDraggedIndex(null)}
                        onDragOver={(event) => event.preventDefault()}
                        onDrop={() => handleDrop(index)}
                        style={{
                            padding: "8px",
                            textAlign: "center",
                            cursor: "grab",
                            border: index === 0 ? "2px solid #1769aa" : "1px solid #bbb",
                            borderRadius: "6px",
                            opacity: draggedIndex === index ? 0.5 : 1
                        }}
                    >
                        <img
                            src={item.preview}
                            alt={`Zdjęcie ${index + 1}`}
                            style={{display: "block", width: "120px", height: "100px", objectFit: "cover", marginBottom: "8px"}}
                        />
                        {index === 0 && <strong style={{display: "block", marginBottom: "6px"}}>Główne</strong>}
                        <button type="button" onClick={() => handleRemoveImage(item)} style={{backgroundColor: "red", color: "white"}}>
                            Usuń
                        </button>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default EditUploadImageToS3;
